import { Component, OnDestroy, OnInit, ChangeDetectorRef, Input, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModelPoolService } from '../../../services/model-pool.service';
import { MuxAgentService } from '../../../services/mux-agent.service';
import { MuxCredentialService } from '../../../services/mux-credential.service';
import { WidgetService } from '../../../services/widget.service';
import { DialogService } from '../../../services/dialog.service';

interface AgentRow {
  agent: MuxAgentService.Agent;
  saving: boolean;
}

/**
 * The Agents lens — section ③ of the Setup tab (Machine → Credentials → Agents).
 * Each agent row surfaces its bound Credential (+ billing badge from the credential's
 * mode) and model, and lets you re-bind the credential. This is what makes a Credential
 * actually *used* — the resolver injects a credential's env only for agents bound to it.
 */
@Component({
  selector: 'app-agents-lens',
  templateUrl: './agents-lens.component.html',
  styleUrls: ['./agents-lens.component.scss'],
  standalone: false,
})
export class AgentsLensComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  rows: AgentRow[] = [];
  credentials: MuxCredentialService.Credential[] = [];
  models: Record<string, MuxAgentService.ModelInfo[]> = {};
  loading = false;
  loadError = false;

  constructor(
        private cdr: ChangeDetectorRef,
private agentsSvc: MuxAgentService,
    private creds: MuxCredentialService,
    private widget: WidgetService,
    private pool: ModelPoolService,
  ) {
    this.pool.groups(true).then(g => { this.poolGroups = g; this.onPoolFilter(this.poolFilter); }).catch(() => {});
  }

  // The model POOL (2026-08-13): ONE dropdown per agent picks the
  // (model, credential) tuple; backend + credentials_id are derived. Same
  // control grammar as the channel override picker.
  poolGroups: ModelPoolService.PoolGroup[] = [];

  poolValueFor(agent: MuxAgentService.Agent): string {
    let fallback = '';
    for (const g of this.poolGroups) {
      for (const e of g.models) {
        const twin = (e.backend === 'claude-cli' || e.backend === 'claude-interactive') &&
                     ((agent.backend || '') === 'claude-cli' || (agent.backend || '') === 'claude-interactive');
        const backendMatch = e.backend === agent.backend || twin;
        if (backendMatch && e.model === agent.model) {
          if ((e.credentialId || '') === (agent.credentials_id || '')) return e.key;
          if (!fallback) fallback = e.key;
        }
      }
    }
    return fallback;
  }

  /** Shown when the agent's current (model, credential) has no entry in the
   *  policy-filtered pool: say what it IS and that the team policy excludes
   *  it, instead of a blank "Pick a model…" that reads as unset. */
  placeholderFor(agent: MuxAgentService.Agent): string {
    if (!agent.model || this.poolValueFor(agent) || !this.pool.lastPolicy?.exists) return 'Pick a model…';
    const cred = agent.credentials_id ? ` · ${agent.credentials_id}` : '';
    return `${agent.model}${cred} — not allowed by team policy`;
  }

  triggerLabel(agent: MuxAgentService.Agent): string {
    const v = this.poolValueFor(agent);
    for (const g of this.poolGroups) {
      const e = g.models.find(x => x.key === v);
      if (e) return this.tupleLabel(e);
    }
    return agent.model || 'Pick a model…';
  }

  // Type-to-filter: recomputed only on input events so *ngFor sees stable
  // array identities between change-detection passes.
  poolFilter = '';
  filteredPoolGroups: ModelPoolService.PoolGroup[] = [];

  private _poolFilterTimer: any = null;

  onPoolFilter(value: string): void {
    this.poolFilter = value;
    clearTimeout(this._poolFilterTimer);
    this._poolFilterTimer = setTimeout(() => this._applyPoolFilter(), 120);
  }

  private _applyPoolFilter(): void {
    const q = this.poolFilter.trim().toLowerCase();
    this.filteredPoolGroups = !q ? this.poolGroups
      : this.poolGroups
          .map(g => ({ provider: g.provider, models: g.models.filter(m =>
              [m.displayName, m.credentialName || '', g.provider]
                .some(t => t.toLowerCase().includes(q))) }))
          .filter(g => g.models.length);
    // The panel is an embedded view in the CDK overlay — force a synchronous
    // pass so the open panel re-renders while the user types.
    this.cdr.detectChanges();
  }

  onPoolFilterKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      // Escape with text = clear the filter, keep the panel open.
      // Escape with an empty box falls through and closes the panel.
      if (this.poolFilter) {
        e.stopPropagation();
        e.preventDefault();
        clearTimeout(this._poolFilterTimer);
        this.poolFilter = '';
        this._applyPoolFilter();
      }
      return;
    }
    e.stopPropagation();
  }

  onPoolPanelToggle(open: boolean): void {
    if (open) {
      // Type-to-filter should just work: focus the filter box once the CDK
      // overlay has rendered (only one panel is ever open).
      setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('.model-select-panel .pool-filter input');
        el?.focus();
      });
    } else if (this.poolFilter) {
      this.onPoolFilter('');
    }
  }

  tupleLabel(e: ModelPoolService.PoolEntry): string {
    return `${e.displayName} · ${e.credentialName || 'local'}`;
  }

  async onPoolSelect(row: AgentRow, value: string): Promise<void> {
    let entry: ModelPoolService.PoolEntry | null = null;
    for (const g of this.poolGroups) {
      const e = g.models.find(x => x.key === value);
      if (e) { entry = e; break; }
    }
    if (!entry) return;
    if (entry.backend === row.agent.backend && entry.model === row.agent.model &&
        (entry.credentialId || '') === (row.agent.credentials_id || '')) return;
    row.saving = true;
    try {
      const full = await this.agentsSvc.getAgent(row.agent.id);
      if (!full) throw new Error('agent not found');
      await this.agentsSvc.updateAgent(row.agent.id, {
        ...full,
        backend: entry.backend,
        model: entry.model,
        credentials_id: entry.credentialId ?? undefined,
      });
      await this.reload();
    } catch {
      this.loadError = true;
    } finally {
      row.saving = false;
    }
  }

  /** Agent detail is its own app — open it in the user's window manager rather than
   *  cramming it into this screen. agentId=null → create mode. */
  openEdit(row: AgentRow): void {
    this.widget.openAgentSettings$.next({ agentId: row.agent.id, team: this.currentTeam });
  }

  openCreate(): void {
    // The dialog is born in THIS panel's team, not the ambient selection.
    this.widget.openAgentSettings$.next({ agentId: null, team: this.currentTeam });
  }

  private _credSub?: Subscription;
  private _agentSub?: Subscription;

  async ngOnInit(): Promise<void> {
    await this.reload();
    // Refresh credential names/options when the pool changes elsewhere.
    this._credSub = this.creds.changed$.subscribe(() => this.reload());
    // The agent dialog is a separate app; its create/save/delete must show here.
    this._agentSub = this.agentsSvc.agentsChanged$.subscribe(() => this.reload());
  }

  ngOnDestroy(): void {
    this._credSub?.unsubscribe();
    this._agentSub?.unsubscribe();
  }

  /** The venue this lens serves — the widget's team when hosted in the Agents
   *  app; ambient identity only as a legacy fallback. */
  @Input() teamName: string | null = null;
  get currentTeam(): string | null {
    if (this.teamName) return this.teamName;
    try { return JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || null; } catch { return null; }
  }

  async scopeToTeam(row: { agent: MuxAgentService.Agent; saving: boolean }): Promise<void> {
    if (!this.currentTeam) return;
    row.saving = true;
    try {
      await this.agentsSvc.scopeAgentToTeam(row.agent.id, this.currentTeam);
      await this.reload();
    } catch (e) {
      console.error('scope-to-team failed:', e);
    } finally {
      row.saving = false;
    }
  }

  async reload(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      const [agents, creds, models] = await Promise.all([
        this.agentsSvc.loadAgents(this.currentTeam || undefined),   // team-scoped: the panel's venue
        this.creds.list(),
        this.agentsSvc.getModels(),
        this.pool.pool(),                       // loads the team policy summary
      ]);
      // Team policy: only credentials the user may bill to are offered/shown.
      this.credentials = this.pool.allowedCredentials(creds);
      this.models = models?.models || {};
      this.modelConstraints = models?.constraints || {};
      this.rows = agents.map((a) => ({ agent: a, saving: false }));
    } catch {
      this.loadError = true;
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  /** Normalize a binding: undefined/'' → null (unbound). */
  private norm(id: string | null | undefined): string | null {
    return id ? id : null;
  }

  /** Real, bindable credentials — excludes the legacy flat-default row. "Unbound"
   *  is offered by the dropdown's own synthetic None option, not a pool credential. */
  get boundableCredentials(): MuxCredentialService.Credential[] {
    return this.credentials.filter((c) => !c.is_default);
  }

  credFor(id: string | null | undefined): MuxCredentialService.Credential | undefined {
    const key = this.norm(id);
    return this.credentials.find((c) => this.norm(c.id) === key);
  }

  modeLabel(m?: MuxCredentialService.Mode): string {
    return m === 'api_key' ? 'Pay-per-token' : m === 'subscription' ? 'Subscription' : m === 'local' ? 'Local' : '';
  }

  isMetered(id: string | null | undefined): boolean {
    return this.credFor(id)?.mode === 'api_key';
  }

  /** Badge text for a row: the bound credential's mode, or "unbound" when the
   *  agent's credentials_id points at no credential in the pool. */
  badgeText(id: string | null | undefined): string {
    const c = this.credFor(id);
    return c ? this.modeLabel(c.mode) : 'unbound';
  }

  isUnbound(id: string | null | undefined): boolean {
    return !this.credFor(id);
  }

  /** Badge the exception, not the rule: only metered / local get a pill.
   *  Subscription (the default) and unbound stay quiet. */
  showBadge(id: string | null | undefined): boolean {
    const c = this.credFor(id);
    return !!c && (c.mode === 'api_key' || c.mode === 'local');
  }

  /** Re-bind an agent to a credential via a FULL-config round-trip (never partial —
   *  POST /agents overwrites model/backend/prompt/mcp for any field it receives). */
  async bind(row: AgentRow, credentialId: string | null): Promise<void> {
    const next = this.norm(credentialId);
    if (this.norm(row.agent.credentials_id) === next) return;
    row.saving = true;
    try {
      const full = await this.agentsSvc.getAgent(row.agent.id);
      if (!full) throw new Error('agent not found');
      await this.agentsSvc.updateAgent(row.agent.id, { ...full, credentials_id: next as any });
      await this.reload();
    } catch {
      this.loadError = true;
    } finally {
      row.saving = false;
    }
  }

  onSelect(row: AgentRow, value: string): void {
    // '' from the <select> means the Default credential (null).
    this.bind(row, value || null);
  }

  /** Models available to an agent — keyed by its backend (which encodes vendor).
   *  Always includes the agent's current model so the <select> can show it even if
   *  the model list doesn't (or hasn't loaded). Vendor-correct for today's pool;
   *  when substrate/credential-derivation lands this keys off the resolved backend. */
  modelConstraints: Record<string, string[]> = {};

  modelsFor(agent: MuxAgentService.Agent): MuxAgentService.ModelInfo[] {
    let list = (agent.backend && this.models[agent.backend]) || [];
    // Restrict to what the bound credential's (vendor, mode) actually supports, so the
    // dropdown never offers a model the provider will 400-reject (e.g. a ChatGPT login
    // serves the GPT-6 family only). Unconstrained (vendor, mode) pairs are unaffected.
    const c = this.credFor(agent.credentials_id);
    const allow = c ? this.modelConstraints[`${c.vendor}:${c.mode}`] : undefined;
    if (allow) {
      list = list.filter((m) => allow.includes(m.name));
    }
    if (agent.model && !list.some((m) => m.name === agent.model)) {
      // Surface the agent's current model if it isn't already listed — UNLESS a constraint
      // excludes it (then we omit it so the user is nudged to a supported choice).
      if (!allow || allow.includes(agent.model)) {
        return [{ name: agent.model }, ...list];
      }
    }
    return list;
  }

  /** Friendly label for the runtime backend — read-only, informational. The backend is
   *  implied by the bound credential (incl. the Claude substrate: CLI vs Interactive). */
  backendLabel(backend: string): string {
    const map: Record<string, string> = {
      'claude-cli': 'Claude CLI',
      'claude-interactive': 'Claude Interactive',
      'claude': 'Claude API',
      'gemini': 'Gemini',
      'antigravity': 'Antigravity',
      'chatgpt': 'ChatGPT (Codex)',
      'openai': 'OpenAI',
      'ollama': 'Ollama',
      'deepseek': 'DeepSeek',
      'perplexity': 'Perplexity',
    };
    return map[backend] || backend;
  }

  modelLabel(m: MuxAgentService.ModelInfo): string {
    return m.display_name || m.name;
  }

  /** Re-bind an agent's model via a FULL-config round-trip (same reason as bind()). */
  async bindModel(row: AgentRow, model: string): Promise<void> {
    if (!model || model === row.agent.model) return;
    row.saving = true;
    try {
      const full = await this.agentsSvc.getAgent(row.agent.id);
      if (!full) throw new Error('agent not found');
      await this.agentsSvc.updateAgent(row.agent.id, { ...full, model });
      await this.reload();
    } catch {
      this.loadError = true;
    } finally {
      row.saving = false;
    }
  }

  onModelSelect(row: AgentRow, value: string): void {
    this.bindModel(row, value);
  }

  async remove(row: AgentRow): Promise<void> {
    const name = row.agent.name || row.agent.id;
    if (!await this.dialogs.confirm(`Delete agent "${name}"? This removes its configuration.`)) return;
    row.saving = true;
    try {
      await this.agentsSvc.deleteAgent(row.agent.id);
      await this.reload();
    } catch {
      this.loadError = true;
    } finally {
      row.saving = false;
    }
  }
}
